import { useEffect, useMemo, useRef, useState } from 'react';
import {
  listAdminProducts,
  updateProduct,
  softDeleteProduct,
  restoreProduct,
  createProduct,
  replaceProductSizes,
  replaceProductImages,
  requestProductImageUpload,
  PRODUCT_IMAGE_LIMIT,
  type AdminProduct,
} from '../api/adminProducts';
import { Shirt } from '../components/Shirt';
import type { ShirtColor, ShirtCut, Size } from '../types';
import { formatGroszeAsZl } from '../util/money';

const SHIRT_COLORS: ShirtColor[] = ['czarna', 'biała'];
const SHIRT_CUTS: ShirtCut[] = ['męski', 'damski'];
const SHIRT_SIZES: Size[] = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL'];
const ACCEPT_IMAGE_TYPES = 'image/jpeg,image/png,image/webp';

// The shop shows one shirt per colour, so the database keeps a single live product per colour.
// Without this the operator gets the bare error code and no idea what to do about it.
function saveErrorMessage(e: unknown, fallback: string): string {
  const message = (e as Error).message;
  if (message === 'duplicate_color') {
    return 'Jest już produkt w tym kolorze. Edytuj istniejący zamiast dodawać drugi.';
  }
  return message || fallback;
}

export function Products() {
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);

  const reload = async () => {
    try {
      setProducts(await listAdminProducts());
    } catch (err) {
      console.error(err);
      setError('Nie udało się załadować produktów.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  if (loading) return <div className="muted">Ładuję produkty…</div>;
  if (error) return <div style={{ color: 'var(--admin-danger)' }}>{error}</div>;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <button className="btn-a primary" onClick={() => setCreatingNew(true)}>
          + Nowy produkt
        </button>
      </div>

      {creatingNew && (
        <NewProductCard
          onCancel={() => setCreatingNew(false)}
          onCreated={() => {
            setCreatingNew(false);
            void reload();
          }}
        />
      )}

      {products.map((p) => (
        <ProductCard key={p.id} product={p} onChange={reload} />
      ))}
    </>
  );
}

// ---------- per-product card ----------

function ProductCard({ product, onChange }: { product: AdminProduct; onChange: () => void }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <div
      className="card product-card"
      style={{
        opacity: product.deletedAt ? 0.55 : 1,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--admin-muted)', letterSpacing: '.1em' }}>
            PRODUKT · {product.id} · KOLOR={product.color}
            {product.deletedAt && <span style={{ color: 'var(--admin-danger)', marginLeft: 8 }}>USUNIĘTY</span>}
          </div>
          <h1 style={{ marginTop: 4 }}>{product.name}</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!editing && !product.deletedAt && (
            <button className="btn-a" onClick={() => setEditing(true)}>
              ✎ Edytuj
            </button>
          )}
          {!product.deletedAt ? (
            <button
              className="btn-a danger"
              disabled={busy}
              onClick={async () => {
                if (!confirm(`Usunąć produkt "${product.name}"?`)) return;
                setBusy(true);
                try {
                  await softDeleteProduct(product.id);
                  onChange();
                } finally {
                  setBusy(false);
                }
              }}
            >
              ✕ Usuń
            </button>
          ) : (
            <button
              className="btn-a"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await restoreProduct(product.id);
                  onChange();
                } finally {
                  setBusy(false);
                }
              }}
            >
              ↺ Przywróć
            </button>
          )}
        </div>
      </div>

      <div className="product-body">
        <ProductImagePanel product={product} onChange={onChange} />

        <div>
          {editing ? (
            <ProductEditor
              product={product}
              onCancel={() => setEditing(false)}
              onSaved={() => {
                setEditing(false);
                onChange();
              }}
            />
          ) : (
            <ProductSummary product={product} />
          )}

          <div style={{ marginTop: 18 }}>
            <h2>Rozmiary</h2>
            <SizesMatrix product={product} onChange={onChange} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- product gallery (up to PRODUCT_IMAGE_LIMIT photos, ordered) ----------

function ProductImagePanel({ product, onChange }: { product: AdminProduct; onChange: () => void }) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const images = product.images;
  const room = PRODUCT_IMAGE_LIMIT - images.length;

  const commit = async (keys: string[]) => {
    setErr(null);
    setBusy(true);
    try {
      await replaceProductImages(product.id, keys);
      onChange();
    } catch (e) {
      setErr((e as Error).message || 'Nie udało się zapisać galerii.');
    } finally {
      setBusy(false);
    }
  };

  const onPick = async (picked: File[]) => {
    setErr(null);
    if (room <= 0) return;
    const files = picked.slice(0, room);
    setBusy(true);
    try {
      // Uploaded one at a time, then committed once: a half-finished batch leaves orphaned
      // S3 objects rather than a gallery the operator did not arrange.
      const uploaded: string[] = [];
      for (const file of files) {
        const { upload_url, key } = await requestProductImageUpload(product.id, file.type);
        const putRes = await fetch(upload_url, {
          method: 'PUT',
          body: file,
          headers: { 'content-type': file.type },
        });
        if (!putRes.ok) throw new Error(`s3_put_${putRes.status}`);
        uploaded.push(key);
      }
      await replaceProductImages(product.id, [...images.map((i) => i.key), ...uploaded]);
      if (picked.length > files.length) {
        setErr(`Dodano ${files.length} z ${picked.length} — limit to ${PRODUCT_IMAGE_LIMIT} zdjęcia.`);
      }
      onChange();
    } catch (e) {
      setErr((e as Error).message || 'Upload nie powiódł się.');
    } finally {
      setBusy(false);
    }
  };

  const removeAt = (index: number) =>
    commit(images.filter((_, i) => i !== index).map((i) => i.key));

  const move = (index: number, delta: number) => {
    const next = images.map((i) => i.key);
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    return commit(next);
  };

  return (
    <div>
      <div
        style={{
          border: '1px solid var(--admin-line)',
          background: '#FAF8F3',
          padding: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 280,
        }}
      >
        {images[0]?.url ? (
          <img
            src={images[0].url}
            alt={product.name}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : (
          <div style={{ width: 220, height: 220 }}>
            <Shirt color={product.color} cut="męski" />
          </div>
        )}
      </div>

      {images.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {images.map((image, i) => (
            <div key={image.id} style={{ width: 92 }}>
              <div
                style={{
                  height: 92,
                  border: i === 0 ? '2px solid var(--admin-ink)' : '1px solid var(--admin-line)',
                  background: '#FAF8F3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {image.url && (
                  <img
                    src={image.url}
                    alt={`${product.name} — zdjęcie ${i + 1}`}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                )}
              </div>
              <div style={{ display: 'flex', gap: 2, marginTop: 4 }}>
                <button
                  className="btn-a"
                  style={tinyBtn}
                  disabled={busy || i === 0}
                  onClick={() => void move(i, -1)}
                  aria-label={`Przesuń zdjęcie ${i + 1} w lewo`}
                >
                  ←
                </button>
                <button
                  className="btn-a"
                  style={tinyBtn}
                  disabled={busy || i === images.length - 1}
                  onClick={() => void move(i, 1)}
                  aria-label={`Przesuń zdjęcie ${i + 1} w prawo`}
                >
                  →
                </button>
                <button
                  className="btn-a danger"
                  style={tinyBtn}
                  disabled={busy}
                  onClick={() => void removeAt(i)}
                  aria-label={`Usuń zdjęcie ${i + 1}`}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        multiple
        accept={ACCEPT_IMAGE_TYPES}
        style={{ display: 'none' }}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length > 0) void onPick(files);
          e.target.value = '';
        }}
      />
      <div style={{ marginTop: 10, display: 'flex', gap: 6, alignItems: 'center' }}>
        <button
          className="btn-a"
          disabled={busy || room <= 0}
          onClick={() => fileRef.current?.click()}
        >
          + Dodaj zdjęcia
        </button>
        <span style={{ fontSize: 12, color: 'var(--admin-muted)' }}>
          {images.length}/{PRODUCT_IMAGE_LIMIT} · pierwsze jest główne
        </span>
      </div>
      {err && <div style={{ color: 'var(--admin-danger)', fontSize: 12, marginTop: 6 }}>{err}</div>}
    </div>
  );
}

const tinyBtn = {
  padding: '2px 0',
  flex: 1,
  fontSize: 11,
  lineHeight: 1.4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

// ---------- product summary (view mode) ----------

function ProductSummary({ product }: { product: AdminProduct }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
      <Meta label="Kolor" value={product.color} />
      <Meta label="Cena" value={formatGroszeAsZl(product.priceGrosze)} accent />
      <Meta label="Materiał" value={product.material || '—'} />
      <Meta label="Druk" value={product.printing || '—'} />
      {product.description && (
        <Meta label="Opis" value={<span style={{ whiteSpace: 'pre-wrap' }}>{product.description}</span>} />
      )}
    </div>
  );
}

// ---------- product edit form ----------

function ProductEditor({
  product,
  onCancel,
  onSaved,
}: {
  product: AdminProduct;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [color, setColor] = useState<ShirtColor>(product.color);
  const [name, setName] = useState(product.name);
  const [description, setDescription] = useState(product.description);
  const [priceZl, setPriceZl] = useState((product.priceGrosze / 100).toFixed(2));
  const [material, setMaterial] = useState(product.material);
  const [printing, setPrinting] = useState(product.printing);
  const [displayOrder, setDisplayOrder] = useState(product.displayOrder);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setErr(null);
    const grosze = Math.round(Number(priceZl.replace(',', '.')) * 100);
    if (!name.trim()) return setErr('Nazwa jest wymagana.');
    if (!Number.isFinite(grosze) || grosze <= 0) return setErr('Cena musi być dodatnia.');
    setBusy(true);
    try {
      await updateProduct(product.id, {
        color,
        name: name.trim(),
        description,
        price_grosze: grosze,
        material,
        printing,
        display_order: displayOrder,
      });
      onSaved();
    } catch (e) {
      setErr(saveErrorMessage(e, 'Nie udało się zapisać.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <FieldRow label="Kolor">
        <ColorPicker value={color} onChange={setColor} />
      </FieldRow>
      <FieldRow label="Nazwa">
        <input className="input-a" value={name} onChange={(e) => setName(e.target.value)} />
      </FieldRow>
      <FieldRow label="Cena (zł)">
        <input
          className="input-a mono"
          inputMode="decimal"
          value={priceZl}
          onChange={(e) => setPriceZl(e.target.value)}
        />
      </FieldRow>
      <FieldRow label="Materiał">
        <input className="input-a" value={material} onChange={(e) => setMaterial(e.target.value)} />
      </FieldRow>
      <FieldRow label="Druk">
        <input className="input-a" value={printing} onChange={(e) => setPrinting(e.target.value)} />
      </FieldRow>
      <FieldRow label="Kolejność">
        <input
          className="input-a mono"
          type="number"
          value={displayOrder}
          onChange={(e) => setDisplayOrder(Number(e.target.value) || 0)}
          style={{ width: 100 }}
        />
      </FieldRow>
      <FieldRow label="Opis">
        <textarea
          className="input-a"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          style={{ resize: 'vertical' }}
        />
      </FieldRow>
      {err && <div style={{ color: 'var(--admin-danger)', fontSize: 12 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn-a primary" onClick={save} disabled={busy}>
          {busy ? 'Zapisywanie…' : 'Zapisz'}
        </button>
        <button className="btn-a" onClick={onCancel} disabled={busy}>
          Anuluj
        </button>
      </div>
    </div>
  );
}

// ---------- sizes matrix ----------

function SizesMatrix({ product, onChange }: { product: AdminProduct; onChange: () => void }) {
  const liveKeys = useMemo(
    () => new Set(product.sizes.filter((s) => s.deletedAt === null).map((s) => `${s.cut}|${s.size}`)),
    [product.sizes],
  );
  const [selected, setSelected] = useState<Set<string>>(liveKeys);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setSelected(liveKeys);
  }, [liveKeys]);

  const toggle = (cut: ShirtCut, size: Size) => {
    const key = `${cut}|${size}`;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const dirty =
    selected.size !== liveKeys.size || [...selected].some((k) => !liveKeys.has(k));

  const save = async () => {
    setErr(null);
    setBusy(true);
    try {
      const sizes: Array<{ cut: ShirtCut; size: Size }> = [];
      for (const cut of SHIRT_CUTS) {
        for (const size of SHIRT_SIZES) {
          if (selected.has(`${cut}|${size}`)) sizes.push({ cut, size });
        }
      }
      await replaceProductSizes(product.id, sizes);
      onChange();
    } catch (e) {
      setErr((e as Error).message || 'Nie udało się zapisać.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ overflow: 'hidden', marginTop: 8, padding: 12 }}>
      <div className="a-table-scroll">
        <table className="sizes-matrix" style={{ width: 'auto' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>krój \ rozmiar</th>
              {SHIRT_SIZES.map((s) => (
                <th key={s} className="size-cell" style={{ textAlign: 'center' }}>
                  {s}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SHIRT_CUTS.map((cut) => (
              <tr key={cut}>
                <td style={{ paddingRight: 12 }}>
                  <b>{cut}</b>
                </td>
                {SHIRT_SIZES.map((size) => {
                  const key = `${cut}|${size}`;
                  const on = selected.has(key);
                  return (
                    <td key={key} className="size-cell" style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggle(cut, size)}
                        aria-label={`${cut} ${size}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {err && <div style={{ color: 'var(--admin-danger)', fontSize: 12, marginTop: 6 }}>{err}</div>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
        <button className="btn-a primary" onClick={save} disabled={busy || !dirty}>
          {busy ? 'Zapisywanie…' : 'Zapisz rozmiary'}
        </button>
        <button
          className="btn-a"
          onClick={() => setSelected(liveKeys)}
          disabled={busy || !dirty}
        >
          Cofnij zmiany
        </button>
        <span className="muted" style={{ alignSelf: 'center', fontSize: 12 }}>
          {selected.size} z {SHIRT_CUTS.length * SHIRT_SIZES.length} kombinacji
        </span>
      </div>
    </div>
  );
}

// ---------- new product form ----------

function NewProductCard({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => void }) {
  const [id, setId] = useState('');
  const [color, setColor] = useState<ShirtColor>('czarna');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [priceZl, setPriceZl] = useState('');
  const [material, setMaterial] = useState('');
  const [printing, setPrinting] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    const grosze = Math.round(Number(priceZl.replace(',', '.')) * 100);
    if (!name.trim()) return setErr('Nazwa wymagana.');
    if (!Number.isFinite(grosze) || grosze <= 0) return setErr('Cena musi być dodatnia.');
    setBusy(true);
    try {
      await createProduct({
        ...(id.trim() ? { id: id.trim() } : {}),
        color,
        name: name.trim(),
        description,
        price_grosze: grosze,
        material,
        printing,
      });
      onCreated();
    } catch (e) {
      setErr(saveErrorMessage(e, 'Nie udało się utworzyć produktu.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: 22, marginBottom: 16 }}>
      <div className="mono" style={{ fontSize: 11, color: 'var(--admin-muted)', letterSpacing: '.1em' }}>
        NOWY PRODUKT
      </div>
      <h1 style={{ marginTop: 4 }}>Dodaj produkt</h1>
      <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
        <FieldRow label="Kolor *">
          <ColorPicker value={color} onChange={setColor} />
        </FieldRow>
        <FieldRow label="ID (opcjonalnie)">
          <input
            className="input-a mono"
            placeholder="auto-generowane ze slugu nazwy"
            value={id}
            onChange={(e) => setId(e.target.value)}
          />
        </FieldRow>
        <FieldRow label="Nazwa *">
          <input className="input-a" value={name} onChange={(e) => setName(e.target.value)} />
        </FieldRow>
        <FieldRow label="Cena (zł) *">
          <input
            className="input-a mono"
            inputMode="decimal"
            value={priceZl}
            onChange={(e) => setPriceZl(e.target.value)}
            style={{ width: 140 }}
          />
        </FieldRow>
        <FieldRow label="Materiał">
          <input className="input-a" value={material} onChange={(e) => setMaterial(e.target.value)} />
        </FieldRow>
        <FieldRow label="Druk">
          <input className="input-a" value={printing} onChange={(e) => setPrinting(e.target.value)} />
        </FieldRow>
        <FieldRow label="Opis">
          <textarea
            className="input-a"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ resize: 'vertical' }}
          />
        </FieldRow>
        {err && <div style={{ color: 'var(--admin-danger)', fontSize: 12 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-a primary" onClick={submit} disabled={busy}>
            {busy ? 'Tworzę…' : 'Utwórz produkt'}
          </button>
          <button className="btn-a" onClick={onCancel} disabled={busy}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- shared ----------

function ColorPicker({ value, onChange }: { value: ShirtColor; onChange: (c: ShirtColor) => void }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {SHIRT_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className="btn-a"
          style={{
            justifyContent: 'center',
            background: value === c ? 'var(--ink)' : 'var(--admin-card)',
            color: value === c ? 'var(--paper)' : 'var(--admin-ink)',
            borderColor: value === c ? 'var(--ink)' : 'var(--admin-line-strong)',
          }}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

function Meta({
  label,
  value,
  accent,
}: {
  label: string;
  value: import('react').ReactNode;
  accent?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        borderBottom: '1px dashed var(--admin-line)',
        paddingBottom: 6,
        gap: 12,
      }}
    >
      <span
        className="mono"
        style={{ fontSize: 11, letterSpacing: '.1em', color: 'var(--admin-muted)', textTransform: 'uppercase' }}
      >
        {label}
      </span>
      <span style={accent ? { color: 'var(--orange-deep)', fontWeight: 700 } : { textAlign: 'right' }}>
        {value}
      </span>
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: import('react').ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
      <span style={{ color: 'var(--admin-muted)', fontFamily: 'var(--display)', letterSpacing: '.06em' }}>
        {label}
      </span>
      {children}
    </label>
  );
}
